import { baseConfig } from '@adornis/config/baseConfig.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { subscribeCompanyRegistrations } from '../../../api/company-registration.js';
import { CompanyRegistrationStatus } from '../../../db/enums/registration-status.js';
import { AdminCompanyLayout } from '../../components/admin-company-layout.js';
import { adminRouter } from './admin-routes.js';

const navigate = (
  e: Event,
  path:
    | 'root'
    | 'createCompany'
    | 'users'
    | 'userCreate'
    | 'categories'
    | 'editImprint'
    | 'editPrivacy'
    | 'editCompanyTerms'
    | 'mailTemplates'
    | 'testCv'
    | 'createAdmin'
    | 'registrations'
    | 'emailSettings'
    | 'embedSnippets'
    | 'designSettings'
    | 'applicationPhase'
    | 'statistics',
) => {
  e.preventDefault();
  adminRouter.goTo(path);
};

export const MwAdminLayout = stateful(() => {
  const registrationsQuery = useQuery(() => subscribeCompanyRegistrations()({ _id: 1, status: 1 }), []);
  const pendingCount = (registrationsQuery.value ?? []).filter(
    r => r.status === CompanyRegistrationStatus.PENDING,
  ).length;

  return AdminCompanyLayout({
    sidebarContent: html`
      <li class="menu-title">
        <span>${m.admin_sidebar_admin_menu()}</span>
      </li>
      <li>
        <a href="/admin" @click=${(e: Event) => navigate(e, 'root')}>
          <span class="icon-[material-symbols--dashboard]"></span>
          ${m.admin_sidebar_dashboard()}
        </a>
      </li>
      <li>
        <a href="/admin/registrations" @click=${(e: Event) => navigate(e, 'registrations')}>
          <span class="icon-[material-symbols--task]"></span>
          ${m.admin_registrations_sidebar()}
          ${pendingCount > 0 ? html`<span class="badge badge-warning badge-sm">${pendingCount}</span>` : nothing}
        </a>
      </li>
      <li>
        <a href="/admin/users" @click=${(e: Event) => navigate(e, 'users')}>
          <span class="icon-[material-symbols--group]"></span>
          ${m.admin_sidebar_users()}
        </a>
      </li>
      <li>
        <a href="/admin/statistics" @click=${(e: Event) => navigate(e, 'statistics')}>
          <span class="icon-[material-symbols--monitoring]"></span>
          ${m.admin_sidebar_statistics()}
        </a>
      </li>
      <li>
        <a href="/admin/categories" @click=${(e: Event) => navigate(e, 'categories')}>
          <span class="icon-[material-symbols--list]"></span>
          ${m.admin_sidebar_categories()}
        </a>
      </li>
      <li>
        <a href="/admin/mail-templates" @click=${(e: Event) => navigate(e, 'mailTemplates')}>
          <span class="icon-[material-symbols--mail]"></span>
          ${m.admin_sidebar_mail_templates()}
        </a>
      </li>
      <li>
        <a href="/admin/email-settings" @click=${(e: Event) => navigate(e, 'emailSettings')}>
          <span class="icon-[material-symbols--image]"></span>
          ${m.admin_email_settings_sidebar()}
        </a>
      </li>
      <li>
        <a href="/admin/embed" @click=${(e: Event) => navigate(e, 'embedSnippets')}>
          <span class="icon-[material-symbols--code]"></span>
          ${m.admin_nav_embed()}
        </a>
      </li>
      <li>
        <a href="/admin/design-settings" @click=${(e: Event) => navigate(e, 'designSettings')}>
          <span class="icon-[material-symbols--palette]"></span>
          ${m.admin_design_settings_sidebar()}
        </a>
      </li>
      <li>
        <a href="/admin/application-phase" @click=${(e: Event) => navigate(e, 'applicationPhase')}>
          <span class="icon-[material-symbols--event-busy]"></span>
          ${m.admin_application_phase_title()}
        </a>
      </li>
      <li>
        <a href="/admin/create-admin" @click=${(e: Event) => navigate(e, 'createAdmin')}>
          <span class="icon-[material-symbols--add-circle]"></span>
          ${m.admin_sidebar_create_admin()}
        </a>
      </li>
      <li>
        <a href="/admin/legal/imprint" @click=${(e: Event) => navigate(e, 'editImprint')}>
          <span class="icon-[material-symbols--error]"></span>
          ${m.admin_sidebar_imprint()}
        </a>
      </li>
      <li>
        <a href="/admin/legal/privacy" @click=${(e: Event) => navigate(e, 'editPrivacy')}>
          <span class="icon-[material-symbols--lock]"></span>
          ${m.admin_sidebar_privacy_policy()}
        </a>
      </li>
      <li>
        <a href="/admin/legal/company-terms" @click=${(e: Event) => navigate(e, 'editCompanyTerms')}>
          <span class="icon-[material-symbols--gavel]"></span>
          ${m.admin_sidebar_company_terms()}
        </a>
      </li>
      <!-- This is currently hidden on production, the endpoint still exists for test purposes -->
      ${baseConfig.get('DEPLOYMENT') !== 'production'
        ? html`<li>
            <a href="/admin/test-cv" @click=${(e: Event) => navigate(e, 'testCv')}>
              <span class="icon-[material-symbols--description]"></span>
              ${m.admin_sidebar_test_cv()}
            </a>
          </li>`
        : nothing}
    `,
    pageTitle: html`
      <div class="flex-1 px-2 mx-2">
        <div class="flex items-center gap-3">
          <div class="flex flex-col">
            <div class="font-bold leading-tight">${m.admin_layout_title()}</div>
            <div class="text-xs text-base-content/60">${m.company_dashboard_brand()}</div>
          </div>
        </div>
      </div>
    `,
    content: adminRouter.outlet.value,
  });
});
